import customerIdRegex from '@eneco/common/validation/customerIdRegex';
import { emailValidation } from '@eneco/common/validation/emailRegex';
import phoneNumberRegex from '@eneco/common/validation/phoneNumberRegex';
import { addressFinderSchema } from '@eneco/components/AddressFinder/AddressFinder';
import { object, string } from 'yup';

import type { CorrectionInvoiceFormContent } from '../correction-invoice-form-content.types';

// Sitecore always sends a field's `requiredMessage`, `''` when the author left it empty; the Sanity
// migration drops empty strings. Restoring `''` keeps yup's behaviour the same on both lanes, where
// `undefined` would make yup fall back to its own English message.
const requiredMessage = (field?: { requiredMessage?: string }) => field?.requiredMessage ?? '';

/** Mirrors `libs/sitecore/components/oxxio/src/CorrectionInvoiceForm/util/schema.ts`, reading the flat content. */
export const getSchema = (content: CorrectionInvoiceFormContent, isSuffixRequired: boolean) => {
  const {
    correctionMeterReadingsFormField,
    yearnoteIssueFormField,
    yearnoteIssueOtherFormField,
    invoiceDateFormField,
    invoiceNumberFormField,
    endDateFormField,
    startDateFormField,
    customerIdFormField,
    salutationFormField,
    initialsFormField,
    firstNameFormField,
    surnameFormField,
    phoneNumberFormField,
    emailAddressFormField,
  } = content;

  const personalDetailsSchema = {
    customerId: string()
      .required(requiredMessage(customerIdFormField))
      .matches(customerIdRegex, { message: customerIdFormField?.validationMessage, excludeEmptyString: true }),
    salutation: string().required(requiredMessage(salutationFormField)),
    initials: string().required(requiredMessage(initialsFormField)),
    firstName: string().required(requiredMessage(firstNameFormField)),
    surnamePreposition: string(),
    surname: string().required(requiredMessage(surnameFormField)),
    ...addressFinderSchema(isSuffixRequired).fields,
    phoneNumber: string()
      .matches(phoneNumberRegex, { message: phoneNumberFormField?.validationMessage })
      .required(requiredMessage(phoneNumberFormField)),
    emailAddress: emailValidation({
      emailMessage: emailAddressFormField?.validationMessage,
      requiredMessage: requiredMessage(emailAddressFormField),
    }),
    ...addressFinderSchema(isSuffixRequired).fields,
  };

  const readingsSchema = {
    readingStartDate: string().when('changeType', {
      is: 'end_result',
      then: schema => schema,
      otherwise: schema => schema.required(requiredMessage(startDateFormField)),
    }),
    readingEndDate: string().when('changeType', {
      is: 'start_position',
      then: schema => schema,
      otherwise: schema => schema.required(requiredMessage(endDateFormField)),
    }),
    electricityLowStart: string(),
    electricityLowEnd: string(),
    electricityNormalStart: string(),
    electricityNormalEnd: string(),
    redeliveryLowStart: string(),
    redeliveryLowEnd: string(),
    redeliveryNormalStart: string(),
    redeliveryNormalEnd: string(),
    gasStart: string(),
    gasEnd: string(),
  };

  return object({
    invoiceId: string().required(requiredMessage(invoiceNumberFormField)),
    invoiceDate: string().required(requiredMessage(invoiceDateFormField)),
    correctionReason: string().required(requiredMessage(yearnoteIssueFormField)),
    otherCorrectionReason: string().when('correctionReason', {
      is: 'other',
      then: schema => schema.required(requiredMessage(yearnoteIssueOtherFormField)),
      otherwise: schema => schema,
    }),
    changeType: string().required(requiredMessage(correctionMeterReadingsFormField)),
    ...personalDetailsSchema,
    ...readingsSchema,
  });
};
